// Standalone 404 page (router catch-all). Rendered outside the app shell
// and outside ProtectedRoute, so it must stand on its own — full-viewport
// hero with the brand mark, a big 404, primary/back actions, and quick
// shortcuts back into the app. Tracks the provided design (desktop +
// mobile): the suggestion list shows on ≥ md, a quick-search box on mobile.

import { useMemo, useState, type FormEvent } from "react";
import { Link, useLocation, useNavigate } from "react-router";
import {
  ArrowLeft,
  Building2,
  CalendarDays,
  ChevronRight,
  Home,
  Search,
  Target,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

interface Shortcut {
  to: string;
  label: string;
  icon: React.ReactNode;
}

const SHORTCUTS: Shortcut[] = [
  {
    to: "/properties",
    label: "Danh sách bất động sản",
    icon: <Building2 aria-hidden="true" className="size-4" />,
  },
  {
    to: "/leads",
    label: "Khách hàng tiềm năng",
    icon: <Target aria-hidden="true" className="size-4" />,
  },
  {
    to: "/appointments",
    label: "Lịch hẹn tuần này",
    icon: <CalendarDays aria-hidden="true" className="size-4" />,
  },
];

export function NotFoundPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [search, setSearch] = useState("");

  // Stamp once on mount — a quiet "reference line" like the design, but
  // honest: the path that 404'd + a local timestamp, not an invented id.
  const stamp = useMemo(
    () =>
      new Date().toLocaleString("vi-VN", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      }),
    [],
  );

  const onSearch = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const q = search.trim();
    navigate(q ? `/properties?search=${encodeURIComponent(q)}` : "/properties");
  };

  return (
    <main className="relative flex min-h-svh flex-col items-center justify-center overflow-hidden bg-background px-5 py-16">
      {/* Faint grid backdrop, fading out toward the edges. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 opacity-60"
        style={{
          backgroundImage:
            "linear-gradient(to right, var(--color-border) 1px, transparent 1px), linear-gradient(to bottom, var(--color-border) 1px, transparent 1px)",
          backgroundSize: "48px 48px",
          maskImage:
            "radial-gradient(ellipse 72% 60% at 50% 42%, #000 0%, transparent 100%)",
          WebkitMaskImage:
            "radial-gradient(ellipse 72% 60% at 50% 42%, #000 0%, transparent 100%)",
        }}
      />

      {/* Brand mark — top-left, desktop only (mobile mock leads with the 404). */}
      <Link
        to="/dashboard"
        className="absolute left-6 top-6 z-10 hidden items-center gap-2.5 md:flex"
      >
        <span
          aria-hidden="true"
          className="flex size-9 items-center justify-center rounded-xl bg-gradient-to-br from-primary to-foreground text-sm font-bold text-primary-foreground shadow-[var(--shadow-soft)]"
        >
          E
        </span>
        <span className="text-lg font-bold tracking-tight">Estate</span>
      </Link>

      <div className="relative z-10 flex w-full max-w-xl flex-col items-center text-center">
        {/* 404 hero with the NOT FOUND pill overlapping the numerals. */}
        <div className="relative select-none">
          <span
            aria-hidden="true"
            className="absolute left-1/2 top-1 z-10 -translate-x-1/2 rounded-full bg-foreground px-3.5 py-1.5 font-mono text-[11px] font-semibold tracking-[0.25em] text-background shadow-[var(--shadow-soft)] md:top-1/3 md:-translate-y-1/2"
          >
            NOT FOUND
          </span>
          <h1
            className="bg-gradient-to-b from-foreground via-ink-soft to-ink-faint/25 bg-clip-text text-[7.5rem] font-black leading-[0.85] tracking-tighter text-transparent sm:text-[10rem] lg:text-[13rem]"
            aria-label="404 — Không tìm thấy trang"
          >
            <span>4</span>
            <span className="bg-gradient-to-b from-ink-faint via-ink-faint/70 to-background bg-clip-text text-transparent">
              0
            </span>
            <span>4</span>
          </h1>
        </div>

        <h2 className="mt-6 text-3xl font-bold tracking-tight sm:text-4xl">
          Không tìm thấy <span className="text-primary">trang này.</span>
        </h2>
        <p className="mt-3 max-w-md text-pretty text-sm leading-relaxed text-muted-foreground sm:text-base">
          Trang có thể đã bị xoá, đường dẫn đã thay đổi, hoặc bạn không có quyền
          truy cập với tài khoản hiện tại.
        </p>

        {/* Actions. DOM order [Back, Home]: mobile stacks Home on top via
            flex-col-reverse; desktop is a row Back-left / Home-right. */}
        <div className="mt-8 flex w-full flex-col-reverse gap-3 sm:max-w-md md:w-auto md:flex-row md:justify-center">
          <Button
            type="button"
            variant="outline"
            size="lg"
            onClick={() => navigate(-1)}
            className="w-full md:w-auto"
          >
            <ArrowLeft aria-hidden="true" />
            <span className="md:hidden">Quay lại trang trước</span>
            <span className="hidden md:inline">Quay lại</span>
          </Button>
          <Button
            asChild
            size="lg"
            className="w-full bg-foreground text-background shadow-[var(--shadow-soft)] hover:bg-foreground/90 md:w-auto"
          >
            <Link to="/dashboard">
              <Home aria-hidden="true" />
              Về Trang chủ
            </Link>
          </Button>
        </div>

        {/* Desktop: quick shortcuts back into the app. */}
        <section className="mt-12 hidden w-full max-w-md flex-col gap-2 md:flex">
          <p className="px-1 text-left font-mono text-[11px] font-medium uppercase tracking-[0.18em] text-ink-faint">
            Bạn có thể tìm
          </p>
          {SHORTCUTS.map((s) => (
            <Link
              key={s.to}
              to={s.to}
              className="group flex items-center gap-3 rounded-xl border border-border bg-card px-4 py-3.5 text-left shadow-[var(--shadow-soft)] transition-colors hover:bg-muted/60"
            >
              <span
                aria-hidden="true"
                className="flex size-9 items-center justify-center rounded-lg bg-muted text-muted-foreground"
              >
                {s.icon}
              </span>
              <span className="flex-1 text-[14px] font-semibold">
                {s.label}
              </span>
              <ChevronRight
                aria-hidden="true"
                className="size-4 text-ink-faint transition-transform group-hover:translate-x-0.5"
              />
            </Link>
          ))}
        </section>

        {/* Mobile: quick search box. */}
        <section className="mt-10 flex w-full flex-col gap-2.5 md:hidden">
          <p className="px-1 text-left text-sm font-semibold">Tìm kiếm nhanh</p>
          <form onSubmit={onSearch} className="relative">
            <Search
              aria-hidden="true"
              className="pointer-events-none absolute left-3.5 top-1/2 size-4 -translate-y-1/2 text-ink-faint"
            />
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Mã BĐS, toà nhà, khách…"
              aria-label="Tìm kiếm nhanh"
              className={cn(
                "h-12 w-full rounded-xl border border-border bg-card pl-10 pr-4 text-sm shadow-[var(--shadow-soft)]",
                "placeholder:text-ink-faint focus-visible:border-ring focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50",
              )}
            />
          </form>
        </section>
      </div>

      {/* Reference line — honest telemetry: the path that 404'd + a stamp. */}
      <p className="absolute bottom-6 left-1/2 z-10 max-w-[92vw] -translate-x-1/2 truncate px-4 text-center font-mono text-[11px] text-ink-faint">
        Lỗi tham chiếu · {location.pathname} · {stamp}
      </p>
    </main>
  );
}
